// src/pages/PresensiReport.js

import React, { useEffect, useMemo, useState } from "react";
import ContentLayout from "../layouts/ContentLayout";
import { apiFetch } from "../utils/wkCommon";
import {
  getUmatDisplayLabel,
  getUmatDisplayLabelLine2,
} from "../utils/wkCommon";

import "../styles/PresensiReport.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_OPTIONS = [
  {
    group: "Per Area",
    items: [
      {
        label: "Taman Palem · 研究班",
        areaCode: "ALE",
        value: "ale_yjb",
        subjectContains: "研究班",
      },
      {
        label: "Tanah Pasir · 研究班",
        areaCode: "AZX",
        value: "azx_yjb",
        subjectContains: "研究班",
      },
      {
        label: "Poris · 研究班",
        areaCode: "AZD",
        value: "azd_yjb",
        subjectContains: "研究班",
      },
      {
        label: "Kelapa Gading · 研究班",
        areaCode: "AJH",
        value: "ajh_yjb",
        subjectContains: "研究班",
      },
      {
        label: "Cisalak · 半天班",
        areaCode: "ALC",
        value: "alc_btb",
        subjectContains: "半天班",
      }
    ],
  },
  {
    group: "Per Grup Usia",
    items: [
      {
        label: "Usia Indah",
        value: "usiaIndah",
        ageGroupFlag: "usiaIndah",
        subjectContains: "長青班"
      },
      {
        label: "Muda-Mudi",
        value: "ciqing",
        ageGroupFlag: "ciqing",
        subjectContains: "慈青班"
      },
      {
        label: "SMP",
        value: "smp_SMA",
        ageGroupFlag: "smp_SMA",
        subjectContains: "成長班"
      },
      {
        label: "Paritta Anak-anak",
        value: "erTongBan",
        ageGroupFlag: "erTongBan",
        subjectContains: "讀經班"
      },
    ],
  },
];

function genderLabel(g) {
  const s = (g || "").toLowerCase();

  if (
    s.includes("f") ||
    s.includes("wanita") ||
    s.includes("perempuan")
  ) {
    return {
      zh: "坤",
      id: "Wanita",
    };
  }

  return {
    zh: "乾",
    id: "Pria",
  };
}

function formatDateHeader(dateStr) {
  const d = new Date(dateStr);

  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return {
    year: String(d.getFullYear()),
    month: MONTHS[d.getMonth()],
    day: String(d.getDate()).padStart(2, "0"),
  };
}

function getWeekday(dateStr) {
  const d = new Date(dateStr);

  return [
    "Min",
    "Sen",
    "Sel",
    "Rab",
    "Kam",
    "Jum",
    "Sab",
  ][d.getDay()];
}

export default function PresensiReport() {
  const currentYear = new Date().getFullYear();

  const [reportValue, setReportValue] = useState("");
  const [selectedYear, setSelectedYear] = useState(
    Math.max(currentYear, 2026)
  );
  const [genderFilter, setGenderFilter] = useState("");

  const [loading, setLoading] = useState(false);

  const [classDates, setClassDates] = useState([]);
  const [rows, setRows] = useState([]);
  const [dateTotals, setDateTotals] = useState({});

  const yearOptions = useMemo(() => {
    const maxYear = Math.max(currentYear, 2026);

    const arr = [];

    for (
      let y = maxYear;
      y >= Math.max(maxYear - 3, 2026);
      y -= 1
    ) {
      arr.push(y);
    }

    return arr;
  }, [currentYear]);

  const parsedReport = useMemo(() => {
    if (!reportValue) return null;

    for (const group of REPORT_OPTIONS) {
      for (const item of group.items) {
        if (item.value === reportValue) {
          return item;
        }
      }
    }

    return null;
  }, [reportValue]);

  const canGenerate = !!parsedReport;
  const hasGenerated =
    canGenerate &&
    (loading ||
      rows.length > 0 ||
      classDates.length > 0);
        
  async function loadReport() {
    if (!canGenerate) return;

    try {
      setLoading(true);

      const filters = [];

      filters.push(
        `filters%5Bevent%5D%5Bagenda%5D%5BagendaDate%5D%5B$between%5D%5B0%5D=${selectedYear}-01-01`
      );

      filters.push(
        `filters%5Bevent%5D%5Bagenda%5D%5BagendaDate%5D%5B$between%5D%5B1%5D=${selectedYear}-12-31`
      );

      //Tampilkan hanya data "Peserta"; tidak perlu data "Petugas"
      filters.push(`filters%5Bpeserta%5D%5B$eq%5D=true`);
      //Tampilkan hanya data partisipan yang aktif (belum dihapus)
      filters.push(`filters%5Bdeleted%5D%5B$null%5D=true`);

      if (parsedReport.areaCode) {
        filters.push(
          `filters%5Bevent%5D%5Bagenda%5D%5Barea%5D%5Bcode%5D%5B$eq%5D=${encodeURIComponent(
            parsedReport.areaCode
          )}`
        );
      }

      if (parsedReport.subjectContains) {
        filters.push(
          `filters%5Bevent%5D%5Bagenda%5D%5Bsubject_zh%5D%5B$containsi%5D=${encodeURIComponent(
            parsedReport.subjectContains
          )}`
        );
      }

      //FILTER TERLALU DALAM TIDAK BERFUNGSI DI STRAPI V5
      //FILTER BY AGE GROUP DIPINDAH KE FRONT END
      /*
      if (parsedReport.ageGroupFlag) {
        filters.push(
          `filters%5Bevent%5D%5Bagenda%5D%5Bcategory_age_groups%5D%5Bfilters%5D%5BflagField%5D%5B$eq%5D=${encodeURIComponent(
            parsedReport.ageGroupFlag
          )}`
        );
      }
      */

      const PAGE_SIZE = 100;

      let page = 1;
      let hasMore = true;

      const allData = [];

      while (hasMore) {
        const url =
          `/api/event-participants?` +
          `${filters.join("&")}` +

          `&pagination%5Bpage%5D=${page}` +
          `&pagination%5BpageSize%5D=${PAGE_SIZE}` +

          `&sort%5B0%5D=event.agenda.agendaDate%3Aasc` +

          // umat
          `&populate%5Bumat%5D%5Bfields%5D%5B0%5D=chineseName` +
          `&populate%5Bumat%5D%5Bfields%5D%5B1%5D=fullName` +
          `&populate%5Bumat%5D%5Bfields%5D%5B2%5D=pinyinName` +
          `&populate%5Bumat%5D%5Bfields%5D%5B3%5D=nickName` +
          `&populate%5Bumat%5D%5Bfields%5D%5B4%5D=gender` +
          `&populate%5Bumat%5D%5Bfields%5D%5B5%5D=birthdate` +
          `&populate%5Bumat%5D%5Bfields%5D%5B6%5D=mobilePhone` +

          // non umat
          `&populate%5Bnon_umat_pre_registration%5D%5Bfields%5D%5B0%5D=nonUmatName` +
          `&populate%5Bnon_umat_pre_registration%5D%5Bfields%5D%5B1%5D=nonUmatGender` +
          `&populate%5Bnon_umat_pre_registration%5D%5Bfields%5D%5B2%5D=nonUmatAge` +
          `&populate%5Bnon_umat_pre_registration%5D%5Bfields%5D%5B3%5D=nonUmatPhoneNo` +

          // agenda
          `&populate%5Bevent%5D%5Bpopulate%5D%5Bagenda%5D%5Bpopulate%5D%5B0%5D=area` +
          `&populate%5Bevent%5D%5Bpopulate%5D%5Bagenda%5D%5Bpopulate%5D%5B1%5D=category_age_groups`;

        const resp = await apiFetch(url);

        const pageData = Array.isArray(resp?.data)
          ? resp.data
          : [];

        allData.push(...pageData);

        if (!pageData.length) {
          hasMore = false;
        } else {
          page += 1;

          await new Promise((resolve) =>
            setTimeout(resolve, 10)
          );
        }
      }

      const data = allData;

      const filteredData = data.filter(
        (item) => {

          if (!parsedReport?.ageGroupFlag) {
            return true;
          }

          const groups =
            item?.event?.agenda
              ?.category_age_groups || [];

          return groups.some(
            (g) =>
              g?.FlagField ===
              parsedReport.ageGroupFlag
          );
        }
      );

      const mapDates = {};
      const mapRows = {};

      const dateGenderTotals = {};

      filteredData.forEach((item) => {
        const umat = item?.umat;
        const nonUmat =
          item?.non_umat_pre_registration;

        const agenda =
          item?.event?.agenda;

        const agendaDate =
          agenda?.agendaDate;

        if (!agendaDate) return;

        mapDates[agendaDate] = true;

        if (!dateGenderTotals[agendaDate]) {
          dateGenderTotals[agendaDate] = {
            pria: 0,
            wanita: 0,
          };
        }

        const isInternal = !!umat;

        let rowKey = "";
        let rowData = null;

        if (isInternal) {
          rowKey =
            `internal_${umat.documentId || umat.id}`;

          rowData = {
            type: "internal",
            gender: umat?.gender || "",
            primaryLabel:
              getUmatDisplayLabel(umat),
            secondaryLabel:
              getUmatDisplayLabelLine2(umat),
            umat,
          };
        } else if (nonUmat) {
          rowKey =
            `public_${nonUmat.documentId || nonUmat.id || nonUmat.nonUmatPhoneNo || nonUmat.nonUmatName}`;

          rowData = {
            type: "public",
            gender:
              nonUmat?.nonUmatGender || "",
            primaryLabel:
              nonUmat?.nonUmatName || "-",
            secondaryLabel:
              [
                nonUmat?.nonUmatAge,
                nonUmat?.nonUmatPhoneNo,
              ]
                .filter(Boolean)
                .join(" · "),
            nonUmat,
          };
        } else {
          return;
        }

        const genderObj = genderLabel(
          rowData.gender
        );

        if (genderObj.id === "Pria") {
          dateGenderTotals[agendaDate].pria += 1;
        }

        if (genderObj.id === "Wanita") {
          dateGenderTotals[agendaDate].wanita += 1;
        }
        
        if (!mapRows[rowKey]) {
          mapRows[rowKey] = {
            rowKey,
            ...rowData,
            attendances: {},
          };
        }

        mapRows[rowKey].attendances[
          agendaDate
        ] = true;
      });

      const sortedDates =
        Object.keys(mapDates).sort();

      const finalRows = Object.values(mapRows)
        .map((r) => ({
          ...r,
          total: Object.keys(
            r.attendances || {}
          ).length,
        }))
        .sort((a, b) => {

          const ga = genderLabel(a.gender).id;
          const gb = genderLabel(b.gender).id;

          if (ga !== gb) {
            return ga.localeCompare(gb);
          }

          return (
            a.primaryLabel || ""
          ).localeCompare(
            b.primaryLabel || ""
          );
        });

      setDateTotals(dateGenderTotals);

      setClassDates(sortedDates);
      setRows(finalRows);      
    } catch (err) {
      console.error(err);
      alert("Gagal load laporan presensi");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canGenerate) {
      loadReport();
    } else {
      setRows([]);
      setClassDates([]);
      setDateTotals({});
    }
  }, [reportValue, selectedYear]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const g = genderLabel(r.gender);

      return (
        !genderFilter ||
        g.id === genderFilter
      );
    });
  }, [rows, genderFilter]);

  return (
    <div className="presensi-report-page">
    <ContentLayout
      title="Presensi Report"
      hideSearch={true}
      singleColumn={true}
      mainNode={
        <div className="prr-page">

          {/* FILTER */}
          <div className="prr-filter-card">

            <div className="prr-filter-grid-simple">

              {/* laporan */}
              <div className="prr-field">
                <label>Laporan</label>

                <select
                  value={reportValue}
                  onChange={(e) =>
                    setReportValue(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    Pilih Laporan
                  </option>

                  {REPORT_OPTIONS.map((group) => (
                    <optgroup
                      key={group.group}
                      label={group.group}
                    >
                      {group.items.map((item) => (
                        <option
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* tahun */}
              <div className="prr-field">
                <label>Tahun</label>

                <select
                  value={selectedYear}
                  onChange={(e) =>
                    setSelectedYear(
                      Number(e.target.value)
                    )
                  }
                >
                  {yearOptions.map((y) => (
                    <option
                      key={y}
                      value={y}
                    >
                      {y}
                    </option>
                  ))}
                </select>

                <div className="prr-field-hint">
                  Data mulai tahun 2026
                </div>
              </div>

              {/* gender */}
              <div className="prr-field">
                <label>Gender</label>

                <select
                  value={genderFilter}
                  onChange={(e) =>
                    setGenderFilter(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    Semua
                  </option>

                  <option value="Pria">
                    Pria
                  </option>

                  <option value="Wanita">
                    Wanita
                  </option>
                </select>
              </div>

            </div>
          </div>

          {/* NOTE */}
          <div className="prr-note">
            <span className="prr-checkmark">
              ✓
            </span>
            = Hadir, kosong = Tidak Hadir
          </div>

          {/* EMPTY */}
          {!canGenerate ? (
            <div className="prr-empty">

              <div className="prr-empty-title">
                Laporan Kelas
              </div>

              <div className="prr-empty-subtitle">
                Pilih laporan dan tahun
                untuk generate laporan
              </div>

            </div>
          ) : (
            <div className="prr-table-card">

              <div className="prr-table-header">

                <div className="prr-table-title">
                  {parsedReport?.label}
                  {" · "}
                  Tahun {selectedYear}
                </div>

              </div>

              <div className="prr-table-wrap">

                <table className="prr-table">

                  <thead>

                    <tr>
                      <th rowSpan="4">
                        Nama Peserta
                      </th>

                      <th rowSpan="4">
                        Gender
                      </th>

                      {classDates.map((d) => {
                        const x =
                          formatDateHeader(d);

                        return (
                          <th key={`${d}-y`}>
                            {x.year}
                          </th>
                        );
                      })}

                      <th rowSpan="4">
                        Total
                        <br />
                        Hadir
                      </th>
                    </tr>

                    <tr>
                      {classDates.map((d) => {
                        const x =
                          formatDateHeader(d);

                        return (
                          <th key={`${d}-m`}>
                            {x.month}
                          </th>
                        );
                      })}
                    </tr>

                    <tr>
                      {classDates.map((d) => {
                        const x =
                          formatDateHeader(d);

                        return (
                          <th key={`${d}-d`}>
                            {x.day}
                          </th>
                        );
                      })}
                    </tr>

                    <tr>
                      {classDates.map((d) => (
                        <th key={`${d}-w`}>
                          {getWeekday(d)}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      <th>
                        總數
                      </th>
                      <th></th>
                      {classDates.map((d) => {
                        const t =
                          dateTotals?.[d] || {};
                        return (
                          <th key={`${d}-t`} className="prr-total-gender">
                            <div>
                              乾: {t.pria || 0}位
                            </div>
                            <div>
                              坤: {t.wanita || 0}位
                            </div>
                          </th>
                        );
                      })}
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>

                    {loading ? (
                      <tr>
                        <td
                          colSpan={
                            classDates.length + 4
                          }
                        >
                          Loading...
                        </td>
                      </tr>
                    ) : filteredRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            classDates.length + 4
                          }
                        >
                          Tidak ada data
                        </td>
                      </tr>
                    ) : (
                      filteredRows.map(
                        (r, idx) => {
                          /*
                          const umat =
                            r.umat || {};

                          const gender =
                            genderLabel(
                              umat?.gender
                            );
                          */
                          const gender =
                            genderLabel(r.gender);
                          
                          return (
                            <tr key={r.rowKey}>
                              <td className="prr-name-cell">

                                <div className="prr-name-1">
                                  {r.primaryLabel}
                                </div>

                                <div className="prr-name-2">
                                  {r.secondaryLabel}
                                </div>

                              </td>

                              <td>
                                <div>
                                  {gender.zh}
                                </div>

                                <div className="prr-gender-sub">
                                  ({gender.id})
                                </div>
                              </td>

                              {classDates.map(
                                (d) => (
                                  <td
                                    key={d}
                                    className="prr-check-cell"
                                  >
                                    {r
                                      .attendances?.[
                                      d
                                    ]
                                      ? "☑"
                                      : ""}
                                  </td>
                                )
                              )}

                              <td className="prr-total-cell">
                                {r.total}
                              </td>

                            </tr>
                          );
                        }
                      )
                    )}

                  </tbody>

                </table>

              </div>

            </div>
          )}

        </div>
      }
    />
    </div>
  );
}